import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import CalendarImg from "../../../../../images/calendar.svg";
import Ical from "../../../../../images/ical.svg";
import IconButton from "../../../../iconButton";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ToggleButton from "../../../../commons/ToggleButton";
import Button from "../../../../button";
import {
  PlusCircleIcon,
  LinkIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import CustomInputField from "../../../../customInputField";
import { toast } from "react-toastify";
import DeleteRegionModal from "../../../../modal/DeleteRegionModal";
import {
  useGetUserDetailsQuery,
  useSaveAdminUserInfoMutation,
} from "../../../../../store/admin/professionalUserInfo";
import CustomNumberField from "../../../../customNumberField";

const ProfessionalCalendarIntegration = ({ isMobile }) => {
  const location = useLocation();
  const { professionalId } = location?.state ?? {};
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const [deleteIsPhoneLoading, setDeleteIsPhoneLoading] = useState(false);
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const [saveIsPhoneLoading, setSaveIsPhoneLoading] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeletePhoneModal, setShowDeletePhoneModal] = useState(false);
  const [saveLink, setSaveLink] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [mode, setMode] = useState("add");
  const [phoneMode, setPhoneMode] = useState("add");
  const [isDelete, setIsDelete] = useState(false);
  const [isPhoneDelete, setIsPhoneDelete] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [action, setAction] = useState(null);

  const [
    saveProfessionalScheduleLink,
    { isLoading: scheduleLinkLoader, isSuccess: scheduleLinkSuccess },
  ] = useSaveAdminUserInfoMutation();

  const { data: professionalInfoDetail, refetch } =
    useGetUserDetailsQuery(professionalId);

  useEffect(() => {
    if (professionalInfoDetail && professionalInfoDetail?.data?.schedule_url) {
      setShowLinkInput(true);
      setMode("edit");
      setSaveLink(professionalInfoDetail?.data?.schedule_url);
    } else {
      setShowLinkInput(false);
      setMode("add");
    }
    if (professionalInfoDetail && professionalInfoDetail?.data?.ehr_sms) {
      setShowPhoneInput(true);
      setPhoneMode("edit");
      setPhoneValue(professionalInfoDetail?.data?.ehr_sms);
    } else {
      setShowPhoneInput(false);
      setPhoneMode("add");
    }
    setSaveIsLoading(false);
    setSaveIsPhoneLoading(false);
    setDeleteIsLoading(false);
    setDeleteIsPhoneLoading(false);
  }, [professionalInfoDetail]);

  const handleShowInput = () => {
    setShowLinkInput(true);
  };

  const handleShowPhoneInput = () => {
    setPhoneValue("");
    setShowPhoneInput(true);
  };

  useEffect(() => {
    if (scheduleLinkSuccess && !scheduleLinkLoader) {
      if (isDelete) {
        toast.success("Schedule Link deleted successfully.");
        handleClose();
        setIsDelete(false);
        refetch();
        return;
      }
      if (isPhoneDelete) {
        toast.success("EHR/EMR deleted successfully.");
        handlePhoneModalClose();
        setIsPhoneDelete(false);
        refetch();
        return;
      }
      if (action == "add_link") {
        toast.success("Schedule Link added successfully.");
        setMode("edit");
      } else if (action == "edit_link") {
        toast.success("Schedule Link updated successfully.");
        setMode("edit");
      } else if (action == "add_ehr") {
        toast.success("EHR/EMR added successfully.");
        setPhoneMode("edit");
      } else if (action == "edit_ehr") {
        toast.success("EHR/EMR updated successfully.");
        setPhoneMode("edit");
      }
      setAction(null);
      setSaveIsLoading(false);
      setSaveIsPhoneLoading(false);
      setDeleteIsLoading(false);
      setDeleteIsPhoneLoading(false);
      refetch();
    }
  }, [scheduleLinkSuccess]);

  const handleAddingScheduleLink = () => {
    if (mode === "edit") {
      setAction("edit_link");
      setMode("add");
      return;
    } else {
      if (action != "edit_link") {
        setAction("add_link");
      }
      if (saveLink === "") {
        setErrorMessage("Schedule Link cannot be empty.");
        return;
      }
      setErrorMessage("");
      const params = {
        _method: "PUT",
        schedule_url: saveLink,
      };
      saveProfessionalScheduleLink({ professionalId, ...params });
      setSaveIsLoading(true);
    }
  };

  const handleAddingPhoneNumber = () => {
    if (phoneMode === "edit") {
      setAction("edit_ehr");
      setPhoneMode("add");
      return;
    } else {
      if (action != "edit_ehr") {
        setAction("add_ehr");
      }
      if (phoneValue === "") {
        setPhoneErrorMessage("EHR/EMR field cannot be empty.");
        return;
      }
      setPhoneErrorMessage("");
      const params = {
        _method: "PUT",
        ehr_sms: phoneValue,
      };
      saveProfessionalScheduleLink({ professionalId, ...params });
      setSaveIsPhoneLoading(true);
    }
  };

  const handleDeletingScheduleLink = () => {
    setDeleteIsLoading(true);
    setSaveLink("");
    const params = {
      _method: "PUT",
      schedule_url: "",
    };
    saveProfessionalScheduleLink({ professionalId, ...params });
  };

  const handleDeletingPhoneNumber = () => {
    setDeleteIsPhoneLoading(true);
    setPhoneValue("");
    const params = {
      _method: "PUT",
      ehr_sms: "",
    };
    saveProfessionalScheduleLink({ professionalId, ...params });
  };

  const handleClose = () => {
    setShowDeleteModal(false);
  };

  const handlePhoneModalClose = () => {
    setShowDeletePhoneModal(false);
  };

  return (
    <div className="">
      {!isMobile && (
        <h4 className="text-[22px] font-bold leading-[160%] text-customGray mb-6">
          Integrations
        </h4>
      )}
      <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex md:items-center justify-between flex-col md:flex-row gap-5 mb-6 bg-white">
        <div
          className={`flex flex-col w-full ${
            mode === "edit" ? "schedule-field" : "dark-field"
          }`}
        >
          <div className="mb-5">
            <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-2">
              Appointment Scheduling Link
            </h6>
            <p className="text-sm md:text-base font-normal leading-[160%] text-customGray mb-[5.5px]">
              You can add your appointment scheduling link here, so it can
              automatically be sent to your leads using the
              &#123;&#123;schedule_link&#125;&#125; shortcode in your
              InstantEngage SMS text and email templates.
            </p>
          </div>
          {showLinkInput && (
            <CustomInputField
              icon={LinkIcon}
              placeHolder="Schedule Link"
              type="text"
              value={saveLink}
              name="schedule_url"
              onChange={(e) => {
                setErrorMessage("");
                setSaveLink(e.target.value);
              }}
              disabled={mode === "edit"}
              classes={`${
                mode === "edit" ? "bg-[#F1F4F6]" : "bg-[#FFFFFF]"
              } flex items-center mx-auto w-full border border-solid border-[#CCD0D4] rounded-[10px] h-[48px] overflow-hidden mb-5`}
              inputClasses={`border-r-0 text-sm font-medium text-customGray placeholder:text-[#869099] pl-[10px] pr-[20px] ${
                mode === "edit" ? "bg-[#F1F4F6]" : "bg-transparent"
              }`}
            />
          )}
          {errorMessage && (
            <p className="text-red-500 font-semibold text-sm mb-2 -mt-2">
              {errorMessage}
            </p>
          )}
          {!showLinkInput && (
            <div>
              <IconButton
                title="add new"
                classes="text-sm font-black leading-[160%] uppercase text-primary flex items-center justify-center rounded-[10px] h-10 mb-2 uppercase border border-solid border-primary hover:bg-[#EBF7FF] capitalize py-[9px] px-[19px]"
                iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
                icon={PlusCircleIcon}
                onClick={handleShowInput}
              />
            </div>
          )}
          <div className="flex items-center gap-4 justify-end">
            {showLinkInput && (
              <Button
                title={mode === "add" ? "Save" : "Edit"}
                type="button"
                classes="text-sm font-black leading-[160%] text-white w-full max-w-[160px] md:max-w-[128px] w-full flex items-center justify-center gap-[6px] bg-primary h-10 rounded-[10px]"
                onClick={handleAddingScheduleLink}
                buttonLoader={saveIsLoading}
              />
            )}
            {professionalInfoDetail?.data?.schedule_url && (
              <IconButton
                icon={XMarkIcon}
                iconClasses="w-6 h-6 text-[#EF2121]"
                title="Delete"
                classes="text-sm font-black leading-[160%] text-[#EF2121] w-full max-w-[160px] md:max-w-[128px] flex items-center justify-center gap-1.5 border border-solid border-[#EF2121] rounded-[10px] h-10"
                onClick={() => {
                  setShowDeleteModal(true);
                  setIsDelete(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex md:items-center justify-between flex-col md:flex-row gap-5 mb-6 bg-white">
        <div
          className={`flex flex-col w-full ${
            mode === "edit" ? "schedule-field" : "dark-field"
          }`}
        >
          <div className="mb-5">
            <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-2">
              EHR/EMR SMS Text Number
            </h6>
            <p className="text-sm md:text-base font-normal leading-[160%] text-customGray mb-[5.5px]">
              You can add your EHR/EMR text number for allowing your leads to
              contact your office using the &#123;&#123;ehr_sms&#125;&#125;
              shortcode in your InstantEngage SMS text and email templates.
            </p>
          </div>
          {showPhoneInput && (
            <CustomNumberField
              icon={DevicePhoneMobileIcon}
              type="tel"
              placeHolder="EHR/EMR SMS Text Number"
              disabled={phoneMode === "edit"}
              value={phoneValue}
              onChange={(e) => {
                setPhoneErrorMessage("");
                setPhoneValue(e.target.value.replace(/\D/g, ""));
              }}
              name="phone"
              classes={`${
                phoneMode === "edit" ? "bg-[#F1F4F6]" : "bg-[#FFFFFF]"
              } flex items-center mx-auto w-full border border-solid border-strokeColor rounded-[10px] h-[48px] overflow-hidden mb-5`}
              inputClasses={`border-r-0 text-sm font-medium text-customGray placeholder:text-[#869099] pl-[10px] pr-[20px] ${
                phoneMode === "edit" ? "bg-[#F1F4F6]" : "bg-transparent"
              }`}
            />
          )}
          {phoneErrorMessage && (
            <p className="text-red-500 font-semibold text-sm mb-2 -mt-2">
              {phoneErrorMessage}
            </p>
          )}
          {!showPhoneInput && (
            <div>
              <IconButton
                title="add new"
                classes="text-sm font-black leading-[160%] uppercase text-primary flex items-center justify-center rounded-[10px] h-10 mb-2 uppercase border border-solid border-primary hover:bg-[#EBF7FF] capitalize py-[9px] px-[19px]"
                iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
                icon={PlusCircleIcon}
                onClick={handleShowPhoneInput}
              />
            </div>
          )}
          <div className="flex items-center gap-4 justify-end">
            {showPhoneInput && (
              <Button
                title={phoneMode === "add" ? "Save" : "Edit"}
                type="button"
                classes="text-sm font-black leading-[160%] text-white w-full max-w-[160px] md:max-w-[128px] w-full flex items-center justify-center gap-[6px] bg-primary h-10 rounded-[10px]"
                onClick={handleAddingPhoneNumber}
                buttonLoader={saveIsPhoneLoading}
              />
            )}
            {professionalInfoDetail?.data?.ehr_sms && (
              <IconButton
                icon={XMarkIcon}
                iconClasses="w-6 h-6 text-[#EF2121]"
                title="Delete"
                classes="text-sm font-black leading-[160%] text-[#EF2121] w-full max-w-[160px] md:max-w-[128px] flex items-center justify-center gap-1.5 border border-solid border-[#EF2121] rounded-[10px] h-10"
                onClick={() => {
                  setShowDeletePhoneModal(true);
                  setIsPhoneDelete(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="relative">
        <div className="absolute z-[10] left-0 top-0 w-full h-full bg-[rgba(0,0,0,0.5)] pointer-events-none flex items-center justify-center">
          <h2 className="text-3xl font-bold text-white text-center">
            Coming Soon
          </h2>
        </div>
        <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex md:items-center justify-between flex-col md:flex-row gap-5 mb-6 bg-white">
          <div className="flex gap-5 items-start">
            <img src={CalendarImg} alt="" />
            <div>
              <h6 className="text-base md:text-lg font-bold leading-[160%] text-customGray mb-[5.5px]">
                Google Calendar
              </h6>
              <span className="text-base font-medium leading-[160%] mailto:text-[#485868]">
                example@gmail.com
              </span>
            </div>
          </div>
          <div>
            <IconButton
              icon={XMarkIcon}
              iconClasses="w-6 h-6 text-[#EF2121]"
              title="Disconnect"
              classes="text-sm font-black leading-[160%] text-[#EF2121] w-full md:w-[128px] flex items-center justify-center gap-1.5 border border-solid border-[#EF2121] rounded-[10px] h-10"
            />
          </div>
        </div>
        <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex items-center justify-between gap-[30px] bg-white mb-[14px]">
          <div className="flex md:items-center flex-col md:flex-row justify-between gap-[6px] w-[calc(100%-62px)]">
            <h5 className="text-base font-medium leading-[160%] text-customGray">
              Calendar Name
            </h5>
            <span className="text=sm font-bold leading-[130%] text-primary">
              Use for Scheduling
            </span>
          </div>
          <ToggleButton />
        </div>
        <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex items-center justify-between bg-white mb-[14px]">
          <h5 className="text-base font-medium leading-[160%] text-customGray">
            Calendar Name
          </h5>
          <div className="flex items-center gap-[30px]">
            <ToggleButton />
          </div>
        </div>
        <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex items-center justify-between bg-white mb-6">
          <h5 className="text-base font-medium leading-[160%] text-customGray">
            Calendar Name
          </h5>
          <div className="flex items-center gap-[30px]">
            <ToggleButton />
          </div>
        </div>
        <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex md:items-center justify-between flex-col md:flex-row gap-5 mb-6 bg-white">
          <div className="flex gap-5 items-start">
            <img src={Ical} alt="" />
            <div>
              <h6 className="text-base md:text-lg font-bold leading-[160%] text-customGray mb-[5.5px]">
                iCal
              </h6>
            </div>
          </div>
          <div>
            <Button
              title="Connect"
              classes="text-sm font-black leading-[160%] text-primary w-full md:w-[128px] flex items-center justify-center gap-1.5 border border-solid border-primary rounded-[10px] h-10"
            />
          </div>
        </div>
      </div>

      <DeleteRegionModal
        isOpen={showDeleteModal}
        handleClose={handleClose}
        handleDelete={handleDeletingScheduleLink}
        text="Do you really want to delete this schedule link?"
        loader={deleteIsLoading}
      />
      <DeleteRegionModal
        isOpen={showDeletePhoneModal}
        handleClose={handlePhoneModalClose}
        handleDelete={handleDeletingPhoneNumber}
        text="Do you really want to delete this EHR/EMR system text number?"
        loader={deleteIsPhoneLoading}
      />
    </div>
  );
};

export default ProfessionalCalendarIntegration;
